import React, { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { BadgeCheck, ShieldOff } from 'lucide-react';
import { Input } from './ui/Input';
import { Select } from './ui/Select';
import { toaster } from './ui/toast';
import { revokeVerifiedDocument, setVerifiedDocument, type SetVerifiedDocumentRequest } from '../api/verifiedDocument';
import { useCapabilities } from '../hooks/useCapabilities';
import {
  canManageVerifiedDocument,
  describeVerifiedDocument,
  parseVerifiedDocument,
  verifiedDocumentErrorMessage,
  type VerifiedDocument,
} from '../utils/userAdmin';
import { DateField } from './ui/DateTimeField';

/**
 * SAM-629 — the user's verified document: shown to everyone who opens the user, and marked /
 * cleared by staff holding MANAGE_USERS (backend: samUserManagement PUT/DELETE
 * /users/{user_id}/verified-document). A verified Dominican cédula is what unlocks the Dominican
 * rate group. The API never returns the number or who verified; the staff member is in the
 * backend's audit log.
 */
export interface VerifiedDocumentCardProps {
  userId: string | number;
  /** sam_core."user".cognitoid (the Cognito sub), to hide the control on the caller's own record. */
  cognitoId?: string | null;
  value: unknown;
  /** Pre-fills the form from what the ID scan read (idtype / idnum on the user row). */
  defaultDocument?: string | null;
  defaultNumber?: string | null;
  onChanged?: (value: VerifiedDocument | null) => void;
}

const formatDateTime = (iso?: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString([], { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const formatDay = (ymd?: string | null) => {
  if (!ymd) return '—';
  const d = new Date(`${ymd.slice(0, 10)}T12:00:00Z`);
  return Number.isNaN(d.getTime()) ? ymd : d.toLocaleDateString([], { day: '2-digit', month: 'short', year: 'numeric' });
};

const Row: React.FC<{ k: string; v?: React.ReactNode }> = ({ k, v }) => (
  <div className="grid grid-cols-[130px_minmax(0,1fr)] gap-3 text-sm py-1">
    <span className="text-[var(--ink-500)]">{k}</span>
    <span className="text-[var(--ink-900)] break-words">{v || '—'}</span>
  </div>
);

const btn = 'h-9 px-3 rounded-[10px] text-sm font-semibold cursor-pointer inline-flex items-center gap-1.5 disabled:opacity-60 disabled:cursor-not-allowed';
const btnGhost = `${btn} border border-solid border-[var(--line)] bg-white text-[var(--ink-900)]`;

const toDocument = (idtype?: string | null): 'cedula' | 'passport' =>
  idtype && /passport/i.test(idtype) ? 'passport' : 'cedula';

export const VerifiedDocumentCard: React.FC<VerifiedDocumentCardProps> = ({
  userId, cognitoId, value, defaultDocument, defaultNumber, onChanged,
}) => {
  const { groups, callerBusinessId, callerSub, isLoading } = useCapabilities();
  const [current, setCurrent] = useState<VerifiedDocument | null>(() => parseVerifiedDocument(value));
  const [mode, setMode] = useState<'view' | 'verify' | 'revoke'>('view');
  const [form, setForm] = useState({ type: toDocument(defaultDocument), number: defaultNumber || '', expires_at: '', reason: '' });
  const [revokeReason, setRevokeReason] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => setCurrent(parseVerifiedDocument(value)), [value]);

  const verify = useMutation({
    mutationFn: (body: SetVerifiedDocumentRequest) => setVerifiedDocument(userId, body),
    onSuccess: (resp) => {
      const next = parseVerifiedDocument(resp?.verified_document);
      setCurrent(next);
      setMode('view');
      setFormError(null);
      onChanged?.(next);
      toaster.positive(
        next?.type === 'cedula' ? 'Document verified. The Dominican rate is now available.' : 'Document recorded.',
        { autoHideDuration: 4000 },
      );
    },
    onError: (error) => {
      const msg = verifiedDocumentErrorMessage(error);
      setFormError(msg);
      toaster.negative(msg, { autoHideDuration: 6000 });
    },
  });

  const revoke = useMutation({
    mutationFn: (reason: string) => revokeVerifiedDocument(userId, reason.trim() || undefined),
    onSuccess: (resp) => {
      setCurrent(null);
      setMode('view');
      setRevokeReason('');
      onChanged?.(null);
      toaster.positive(resp?.revoked === false ? 'There was no verification to clear.' : 'Verification cleared.', { autoHideDuration: 4000 });
    },
    onError: (error) => toaster.negative(verifiedDocumentErrorMessage(error), { autoHideDuration: 6000 }),
  });

  const permission = isLoading
    ? { allowed: false }
    : canManageVerifiedDocument({ groups, callerBusinessId, callerSub }, cognitoId);
  const summary = current ? describeVerifiedDocument(current) : null;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.number.trim()) {
      setFormError('Enter the number printed on the document.');
      return;
    }
    verify.mutate({
      type: form.type,
      number: form.number.trim(),
      country: 'DO',
      ...(form.expires_at ? { expires_at: form.expires_at } : {}),
      ...(form.reason.trim() ? { reason: form.reason.trim() } : {}),
    });
  };

  const badge = !summary
    ? { text: 'Not verified', cls: 'bg-[var(--line-subtle)] text-[var(--ink-500)]' }
    : summary.isExpired
      ? { text: 'Expired', cls: 'bg-[var(--warn-bg-2)] text-[var(--warn-fg)]' }
      : summary.grantsDominicanRate
        ? { text: 'Verified · Dominican rate', cls: 'bg-[var(--ok-bg)] text-[var(--ok-fg)]' }
        : { text: 'Recorded', cls: 'bg-[var(--line-subtle)] text-[var(--ink-700)]' };

  return (
    <section aria-labelledby="vd-title" className="rounded-xl border border-solid border-[var(--line)] bg-white p-5 flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2 mb-1">
        <h2 id="vd-title" className="m-0 text-[15px] font-bold flex items-center gap-2">
          <BadgeCheck size={16} aria-hidden /> Document verification
        </h2>
        <span data-testid="vd-badge" className={`text-xs font-semibold px-2.5 py-0.5 rounded-full whitespace-nowrap shrink-0 ${badge.cls}`}>{badge.text}</span>
      </div>

      {summary ? (
        <>
          <Row k="Document" v={summary.documentLabel} />
          <Row k="Verified by" v={summary.sourceLabel === 'ID scan' ? 'ID scan (automatic)' : summary.sourceLabel} />
          <Row k="Verified on" v={formatDateTime(summary.verifiedAt)} />
          {summary.expiresAt && <Row k="Expires" v={formatDay(summary.expiresAt)} />}
          {!summary.grantsDominicanRate && (
            <p className="m-0 text-xs text-[var(--ink-500)]">Only a Dominican cédula unlocks the Dominican rate.</p>
          )}
        </>
      ) : (
        <p className="m-0 text-sm text-[var(--ink-500)]">
          No verified document. The Dominican rate needs a verified Dominican cédula, from the ID scan or from reception.
        </p>
      )}

      {permission.allowed && mode === 'view' && (
        <div className="flex flex-wrap gap-2 mt-2">
          <button type="button" className={btnGhost} onClick={() => { setFormError(null); setMode('verify'); }}>
            <BadgeCheck size={15} aria-hidden /> {summary ? 'Verify again' : 'Mark as verified'}
          </button>
          {summary && (
            <button type="button" className={`${btn} border border-solid border-[var(--bad-fg)] bg-white text-[var(--bad-fg)]`} onClick={() => setMode('revoke')}>
              <ShieldOff size={15} aria-hidden /> Clear verification
            </button>
          )}
        </div>
      )}
      {!permission.allowed && /own document/.test(permission.reason || '') && (
        <p className="m-0 mt-1 text-xs text-[var(--ink-500)]">{permission.reason}</p>
      )}

      {permission.allowed && mode === 'verify' && (
        <form onSubmit={submit} aria-label="Mark document as verified" className="flex flex-col gap-3 mt-2 border-t border-solid border-x-0 border-b-0 border-[var(--line)] pt-3">
          <p className="m-0 text-xs text-[var(--ink-500)]">
            Check the physical document in front of you. The number is validated and stored only as a fingerprint.
          </p>
          <Select
            label="Document"
            value={form.type}
            onChange={(e) => setForm({ ...form, type: e.target.value as 'cedula' | 'passport' })}
            options={[{ label: 'Dominican cédula', value: 'cedula' }, { label: 'Dominican passport', value: 'passport' }]}
          />
          <Input
            label="Document number"
            required
            value={form.number}
            autoComplete="off"
            onChange={(e) => setForm({ ...form, number: e.target.value })}
            hint={form.type === 'cedula' ? '11 digits, e.g. 001-1234567-8' : undefined}
          />
          <div>
            <DateField
              label="Expiry date" ariaLabel="Expiry date" value={form.expires_at}
              onChange={(v) => setForm({ ...form, expires_at: v })}
            />
            <p className="mt-1 text-xs text-[var(--ink-500)]">Only if the document shows one.</p>
          </div>
          <Input
            label="Note"
            value={form.reason}
            maxLength={500}
            onChange={(e) => setForm({ ...form, reason: e.target.value })}
            hint="Optional, kept in the audit log."
          />
          {formError && <div role="alert" className="text-sm text-[var(--bad-fg)]">{formError}</div>}
          <div className="flex justify-end gap-2">
            <button type="button" className={btnGhost} disabled={verify.isPending} onClick={() => setMode('view')}>Cancel</button>
            <button type="submit" disabled={verify.isPending} className={`${btn} border-0 bg-[var(--tenant-primary,#005488)] text-white`}>
              {verify.isPending ? 'Saving…' : 'Mark as verified'}
            </button>
          </div>
        </form>
      )}

      {permission.allowed && mode === 'revoke' && (
        <div role="group" aria-label="Clear verification" className="flex flex-col gap-3 mt-2 border-t border-solid border-x-0 border-b-0 border-[var(--line)] pt-3">
          <p className="m-0 text-sm text-[var(--ink-700)]">
            The user loses the Dominican rate for new bookings. A later ID scan of the same document will not verify it again.
          </p>
          <Input
            label="Reason"
            value={revokeReason}
            maxLength={500}
            onChange={(e) => setRevokeReason(e.target.value)}
            hint="Optional, kept in the audit log (e.g. document expired)."
          />
          <div className="flex justify-end gap-2">
            <button type="button" className={btnGhost} disabled={revoke.isPending} onClick={() => setMode('view')}>Keep verified</button>
            <button
              type="button"
              disabled={revoke.isPending}
              onClick={() => revoke.mutate(revokeReason)}
              className={`${btn} border-0 bg-[var(--bad-fg)] text-white`}
            >
              {revoke.isPending ? 'Clearing…' : 'Confirm clear'}
            </button>
          </div>
        </div>
      )}
    </section>
  );
};
